/**
 * Hook for managing club visit history in localStorage.
 * Matches mobile's AsyncStorage-based pattern for tracking most recently visited clubs.
 */

import { useState, useEffect, useCallback } from "react";

const CLUB_VISITS_KEY = "clubVisitsHistory";

type ClubVisitHistory = {
  [clubId: string]: number; // timestamp of last visit
};

/**
 * Get the club visit history from localStorage
 */
function getClubVisitHistory(): ClubVisitHistory {
  try {
    const historyStr = localStorage.getItem(CLUB_VISITS_KEY);
    return historyStr ? JSON.parse(historyStr) : {};
  } catch (error) {
    console.error("Failed to get club visit history:", error);
    return {};
  }
}

/**
 * Save a club visit to localStorage
 */
function saveClubVisit(clubId: string): void {
  try {
    const history = getClubVisitHistory();
    history[clubId] = Date.now();
    localStorage.setItem(CLUB_VISITS_KEY, JSON.stringify(history));
  } catch (error) {
    console.error("Failed to save club visit:", error);
  }
}

/**
 * Hook to access and manage club visit history
 */
export function useClubVisitHistory() {
  const [visitHistory, setVisitHistory] = useState<ClubVisitHistory>({});

  // Load history on mount
  useEffect(() => {
    setVisitHistory(getClubVisitHistory());
  }, []);

  // Record a club visit
  const recordVisit = useCallback((clubId: string) => {
    saveClubVisit(clubId);
    setVisitHistory(getClubVisitHistory());
  }, []);

  // Get sorted club IDs by recency (most recent first)
  const sortClubsByRecency = useCallback(
    <T extends { id?: string }>(clubs: T[]): T[] => {
      return [...clubs].sort((a, b) => {
        const aId = a.id || "";
        const bId = b.id || "";
        const aTimestamp = visitHistory[aId] || 0;
        const bTimestamp = visitHistory[bId] || 0;
        return bTimestamp - aTimestamp; // most recent first
      });
    },
    [visitHistory]
  );

  // Get most recent club ID
  const getMostRecentClubId = useCallback((): string | null => {
    const entries = Object.entries(visitHistory);
    if (entries.length === 0) return null;

    const sorted = entries.sort(([, a], [, b]) => b - a);
    const first = sorted[0];
    return first ? first[0] : null;
  }, [visitHistory]);

  return {
    visitHistory,
    recordVisit,
    sortClubsByRecency,
    getMostRecentClubId,
  };
}
